import api from "services/api"
import logger from "utils/logger"

const ACCESS_TOKEN_KEY = "authUser"
const REFRESH_TOKEN_KEY = "refreshToken"

/**
 * Issue (store) tokens returned from a successful login/token-endpoint response.
 *
 * @param {{ token: string, refreshToken?: string }} credentials
 */
export const issueToken = credentials => {
  if (!credentials) return
  const { token, refreshToken, ...rest } = credentials
  if (token) {
    const existing = getStoredAuthUser()
    const updated = { ...(existing || {}), ...rest, token }
    localStorage.setItem(ACCESS_TOKEN_KEY, JSON.stringify(updated))
  }
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  }
}

/**
 * Call the backend refresh endpoint and store the new access token.
 *
 * @param {string} [refreshToken] - Falls back to the stored refresh token.
 * @returns {Promise<string>} The new access token.
 */
export const refreshAccessToken = async refreshToken => {
  const token = refreshToken || localStorage.getItem(REFRESH_TOKEN_KEY)
  if (!token) throw new Error("No refresh token available")
  const response = await api.post("auth/refresh-token", { refreshToken: token })
  if (response?.status && response?.data?.token) {
    issueToken(response.data)
    return response.data.token
  }
  throw new Error(response?.msg || "Token refresh failed")
}

/**
 * Validate a token against the backend.
 *
 * @param {string} [token] - Falls back to the stored access token.
 * @returns {Promise<boolean>}
 */
export const validateToken = async token => {
  const accessToken = token || getAuthToken()
  if (!accessToken) return false
  try {
    const response = await api.post("auth/validate-token", { token: accessToken })
    return !!(response?.status)
  } catch (err) {
    logger.error("Token validation error", err)
    return false
  }
}

/**
 * Parse a JWT and return its payload.
 *
 * @param {string} token
 * @returns {object|null}
 */
export const parseToken = token => {
  if (!token) return null
  try {
    const parts = token.split(".")
    if (parts.length !== 3) return null
    const payload = parts[1]
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
    return JSON.parse(decoded)
  } catch (err) {
    logger.error("Token parse error", err)
    return null
  }
}

/**
 * Return the expiry timestamp (in ms) of a token, or null if not parseable.
 *
 * @param {string} token
 * @returns {number|null}
 */
export const getTokenExpiry = token => {
  const payload = parseToken(token)
  if (!payload?.exp) return null
  return payload.exp * 1000
}

/**
 * Check whether a token is expired (or will expire within the given buffer).
 *
 * @param {string} token
 * @param {number} [bufferMs=0] - Extra buffer in milliseconds before reporting expiry.
 * @returns {boolean}
 */
export const isTokenExpired = (token, bufferMs = 0) => {
  const expiry = getTokenExpiry(token)
  if (expiry === null) return true
  return Date.now() + bufferMs >= expiry
}

/**
 * Remove all stored tokens and auth user data from localStorage.
 */
export const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem("isLoggedIn")
  localStorage.removeItem("accountType")
  localStorage.removeItem("accountScope")
  localStorage.removeItem("tenantId")
  localStorage.removeItem("tenantBranding")
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** @returns {string|null} The raw JWT access token. */
export const getAuthToken = () => {
  const authUser = getStoredAuthUser()
  return authUser?.token || null
}

/** @returns {object|null} The parsed authUser object from localStorage. */
const getStoredAuthUser = () => {
  try {
    const raw = localStorage.getItem(ACCESS_TOKEN_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}
